import { addScript } from "../../bootstrap-utils";

/**
 * bt-renderer defines a single `BTRenderer` global, so a document can hold exactly one
 * renderer at a time. Hence one slot for the URL actually injected rather than a cache
 * keyed by URL: a keyed cache would inject every requested script and hand callers
 * whichever one happened to parse last.
 */
let fallbackScriptUrl: string | undefined;
let activeScriptUrl: string | undefined;
let loadPromise: Promise<void> | undefined;

const RELOAD_MARKER_KEY = "betby-renderer-reload";

/** Registers the operator-wide URL used when no per-line URL is supplied. */
export function setBetbyScriptUrl(url: string): void {
  fallbackScriptUrl = url;
}

export function getBetbyScriptUrl(): string | undefined {
  return fallbackScriptUrl;
}

function resolveScriptUrl(requestedUrl?: string): string | undefined {
  return requestedUrl?.trim() || fallbackScriptUrl?.trim() || undefined;
}

/**
 * Loads the Betby bt-renderer script on demand, preferring the caller's per-line URL over
 * the operator-wide fallback. If neither is set (e.g. the operator loads bt-renderer via
 * another mechanism) this is a no-op and the caller's polling will find it already available.
 *
 * Once one URL is in the document a differing one is never injected — the caller decides
 * how to handle that, see getBetbyScriptLineConflict.
 */
export function loadBetbyScript(requestedUrl?: string): Promise<void> {
  const url = resolveScriptUrl(requestedUrl);
  if (!url) {
    return Promise.resolve();
  }
  if (loadPromise) {
    return activeScriptUrl === url ? loadPromise : Promise.resolve();
  }
  activeScriptUrl = url;
  loadPromise = addScript(url).catch((err) => {
    loadPromise = undefined;
    activeScriptUrl = undefined;
    throw err;
  });
  return loadPromise;
}

/**
 * Returns the renderer URL already in the document when it differs from the one requested,
 * otherwise undefined. Synchronous so callers can branch before starting their own polling.
 */
export function getBetbyScriptLineConflict(requestedUrl?: string): string | undefined {
  const url = resolveScriptUrl(requestedUrl);
  if (!url || !activeScriptUrl || activeScriptUrl === url) {
    return undefined;
  }
  return activeScriptUrl;
}

/**
 * A reload swaps the renderer only if it has not already been attempted for this target —
 * otherwise a line that keeps resolving differently would reload forever.
 */
export function shouldReloadForBetbyLineChange(requestedUrl: string, marker: string | null): boolean {
  return marker !== requestedUrl;
}

function readReloadMarker(): string | null {
  try {
    return window.sessionStorage.getItem(RELOAD_MARKER_KEY);
  } catch {
    return null;
  }
}

function writeReloadMarker(url: string): boolean {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Reloads the document so it can be rebuilt around the requested renderer. At most one
 * reload per session per target; when the guard declines, the caller's error path is left
 * to surface instead.
 */
export function reloadForBetbyLineChange(requestedUrl?: string): void {
  const url = resolveScriptUrl(requestedUrl);
  if (!url) {
    return;
  }
  if (!shouldReloadForBetbyLineChange(url, readReloadMarker())) {
    console.error(`Betby renderer still mismatched after reloading for ${url}`);
    return;
  }
  // Session storage is unavailable in some privacy modes; without a marker the one-shot
  // guard cannot hold, and an unguarded reload loops.
  if (!writeReloadMarker(url)) {
    console.error(`Betby renderer mismatch cannot be resolved without session storage: ${url}`);
    return;
  }
  window.location.reload();
}

/** Resets module state — for use in tests only. */
export function _resetBetbyScriptLoader(): void {
  fallbackScriptUrl = undefined;
  activeScriptUrl = undefined;
  loadPromise = undefined;
}
